import CustomTooltip from '../../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useEditPlatformSettingsMutation,
    useGetPlatformSettingsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleHttpError } from '../../../../shared/utils';
import FormTextField from '../../../shared/FormTextField';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';

const AppFeeForm = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedInUser.role !== 'admin') {
            navigate(`/permissionDenied`);
        }
    }, []);

    const { data: platformSettings, isLoading } = useGetPlatformSettingsQuery(
        '/platform-settings',
        { skip: loggedInUser.role !== 'admin' },
    );
    const [editPlatformSettings] = useEditPlatformSettingsMutation();

    const schema = z.object({
        guestServiceFeePercentage: z.coerce.number(),
        hostServiceFeePercentage: z.coerce.number(),
    });
    type PlatformSettings = z.infer<typeof schema>;
    const {
        reset,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<PlatformSettings>({
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        if (platformSettings) {
            const { guestServiceFeePercentage, hostServiceFeePercentage } =
                platformSettings;
            reset({ guestServiceFeePercentage, hostServiceFeePercentage });
        }
    }, [platformSettings]);

    const onSubmit = async (data: PlatformSettings) => {
        editPlatformSettings(data)
            .unwrap()
            .then((data: any) => {
                if (data?.error) {
                    const errorMessage = handleHttpError(data?.error);
                    toast.error(errorMessage);
                } else {
                    toast.success(`Successfully updated platform settings`);
                }
            })
            .catch(() => {
                toast.error(
                    'Failed to update platform settings. Please try again.',
                );
            });
    };

    return isLoading ? (
        <LoadingComponent />
    ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '40px',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                        justifyContent: 'left',
                        display: 'flex',
                    }}
                >
                    <Typography
                        variant='h1'
                        sx={{
                            textAlign: 'left',
                            textTransform: 'uppercase',
                        }}
                    >
                        7DAY App Fees
                    </Typography>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>
                                Guest Service Fee %
                            </Typography>
                            <Grid>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            7DAY guest service fee is a
                                            percentage added to the overall
                                            price/fee per transaction made on
                                            7DAY. This is a source of revenue
                                            from guest companies for 7DAY.
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Grid>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            <FormTextField
                                name='guestServiceFeePercentage'
                                control={control}
                                placeholder='7'
                                type='number'
                            />
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={12}
                            lg={5}
                            sx={{ display: 'flex', gap: '2px' }}
                        >
                            <Typography color='text.primary'>
                                Host Service Fee %
                            </Typography>
                            <Grid>
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            7DAY host service fee is a
                                            percentage cut of the overall
                                            price/fee (excluding guest service
                                            fee) per transaction made on 7DAY.
                                            This is a source of revenue from
                                            host companies for 7DAY.
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Grid>
                        </Grid>
                        <Grid item xs={25} lg={9}>
                            <FormTextField
                                name='hostServiceFeePercentage'
                                control={control}
                                placeholder='3'
                                type='number'
                            />
                        </Grid>
                    </Grid>
                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={25}
                            lg={14}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomButton
                                type='submit'
                                variant='contained'
                                color='green'
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                Update
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default AppFeeForm;
